import * as React from "react";
import { DismissRegular } from "@fluentui/react-icons";
import {
    useId,
    Toaster,
    useToastController,
    Toast,
    ToastTitle,
    ToastTrigger,
    Button,
    makeStyles,
    tokens,
    ToastBody,
} from "@fluentui/react-components";

const useStyles = makeStyles({
    success: {
        "border-color": tokens.colorStatusSuccessBorder1,
        backgroundColor: tokens.colorStatusSuccessBackground1,
    },
    warning: {
        "border-color": tokens.colorStatusWarningBorder1,
        backgroundColor: tokens.colorStatusWarningBackground1,
    },
    error: {
        "border-color": tokens.colorStatusDangerBorder1,
        backgroundColor: tokens.colorStatusDangerBackground1,
    },
});

/**
 * Allowed toast intent values.
 * Wraps Fluent's ToastIntent to a controlled subset.
 */
export type NewformaToastIntent = "success" | "info" | "warning" | "error";

/**
 * Imperative handle exposed by NewformaToast via ref.
 * Use this from class components through React.createRef<NewformaToastHandle>().
 */
export interface NewformaToastHandle {
    showToast(toast: { title: string; intent: NewformaToastIntent; body?: React.ReactNode }): void;
    showMultipleToasts(toasts: { title: string; intent: NewformaToastIntent; body?: React.ReactNode }[]): void;
    dismissSuccess(): void;
    dismissAll(): void;
}

/**
 * Wrapper around Fluent 2's Toast / Toaster imperative API.
 *
 * Renders a single <Toaster> and exposes showToast / dismissAll through a ref.
 */
export const NewformaToast = React.forwardRef<NewformaToastHandle>(function NewformaToast(_props, ref) {
    const toasterId = useId("newforma-toaster");
    const { dispatchToast, dismissAllToasts, dismissToast } = useToastController(toasterId);
    const successToastIdsRef = React.useRef(new Set<string>());
    const successToastCounterRef = React.useRef(0);
    const styles = useStyles();

    const showToast = React.useCallback(
        ({ title, intent, body }: { title: string; intent: NewformaToastIntent; body?: React.ReactNode }) => {
            const isSuccess = intent === "success";
            const toastId = isSuccess
                ? `newforma-success-toast-${Date.now()}-${successToastCounterRef.current++}`
                : undefined;

            let className = "";
            if (intent === "success") className = styles.success;
            else if (intent === "error") className = styles.error;
            else if (intent === "warning") className = styles.warning;

            dispatchToast(
                <Toast className={className}>
                    <ToastTitle
                        action={
                            <ToastTrigger>
                                <Button aria-label="dismiss" appearance="transparent" icon={<DismissRegular />} />
                            </ToastTrigger>
                        }
                    >
                        {title}
                    </ToastTitle>
                    {body && <ToastBody>{body}</ToastBody>}
                </Toast>,
                {
                    toastId,
                    intent,
                    timeout: -1,
                }
            );

            if (isSuccess && toastId) {
                successToastIdsRef.current.add(toastId);
            }
        },
        [dispatchToast]
    );

    const showMultipleToasts = React.useCallback(
        (toasts: { title: string; intent: NewformaToastIntent; body?: React.ReactNode }[]) => {
            toasts.forEach((toast, index) => {
                setTimeout(() => {
                    showToast(toast);
                }, index * 200);
            });
        },
        [showToast]
    );

    const dismissAll = React.useCallback(() => {
        dismissAllToasts();
        successToastIdsRef.current.clear();
    }, [dismissAllToasts]);

    const dismissSuccess = React.useCallback(() => {
        successToastIdsRef.current.forEach((toastId) => dismissToast(toastId));
        successToastIdsRef.current.clear();
    }, [dismissToast]);

    React.useImperativeHandle(ref, () => ({ showToast, showMultipleToasts, dismissSuccess, dismissAll }), [
        showToast,
        showMultipleToasts,
        dismissSuccess,
        dismissAll,
    ]);

    return <Toaster toasterId={toasterId} position="top-end" className="newforma-toaster" />;
});
